import React, { useState, useEffect } from 'react';

interface LikeButtonProps {
  blogPostId: string;
  initialLikeCount?: number;
  initialLiked?: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const LikeButton: React.FC<LikeButtonProps> = ({
  blogPostId,
  initialLikeCount = 0,
  initialLiked = false,
  size = 'medium',
  className = ''
}) => {
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [isLiked, setIsLiked] = useState(initialLiked);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');

  // Generate session ID for anonymous users
  useEffect(() => {
    let storedSessionId = localStorage.getItem('blog_session_id');
    if (!storedSessionId) {
      storedSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('blog_session_id', storedSessionId);
    }
    setSessionId(storedSessionId);

    // Load current like status
    loadLikeStatus(storedSessionId);
  }, [blogPostId]);

  const loadLikeStatus = async (sessionId: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(
        `${apiUrl}/api/blog/${blogPostId}/likes?session_id=${sessionId}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setLikeCount(data.like_count);
          setIsLiked(data.user_has_liked);
        }
      }
    } catch (error) {
      console.error('Error loading like status:', error);
    }
  };

  const handleLikeToggle = async () => {
    if (isLoading || !sessionId) return;

    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      if (isLiked) {
        // Unlike the post
        const response = await fetch(
          `${apiUrl}/api/blog/${blogPostId}/like?session_id=${sessionId}`,
          { method: 'DELETE' }
        );
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setLikeCount(data.like_count);
            setIsLiked(data.user_has_liked);
          }
        }
      } else {
        // Like the post
        const response = await fetch(
          `${apiUrl}/api/blog/${blogPostId}/like`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              blog_post_id: blogPostId,
              session_id: sessionId
            })
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setLikeCount(data.like_count);
            setIsLiked(data.user_has_liked);
          }
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Size variants
  const sizeClasses = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg'
  };

  const iconSizes = {
    small: 'w-4 h-4',
    medium: 'w-5 h-5',
    large: 'w-6 h-6'
  };

  const buttonSizes = {
    small: 'px-2 py-1',
    medium: 'px-3 py-2',
    large: 'px-4 py-2'
  };

  return (
    <button
      onClick={handleLikeToggle}
      disabled={isLoading}
      className={`
        inline-flex items-center gap-2 rounded-lg border transition-all duration-200
        ${buttonSizes[size]} ${sizeClasses[size]}
        ${isLiked
          ? 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100'
          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
        }
        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      title={isLiked ? 'Unlike this post' : 'Like this post'}
    >
      {/* Heart Icon */}
      <svg
        className={`${iconSizes[size]} transition-all duration-200 ${
          isLiked ? 'text-red-500' : 'text-gray-400'
        }`}
        fill={isLiked ? 'currentColor' : 'none'}
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      </svg>
      
      {/* Like Count */}
      <span className="font-medium">
        {likeCount}
      </span>
      
      {/* Loading Indicator */}
      {isLoading && (
        <div className="animate-spin">
          <svg className="w-3 h-3" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      )}
    </button>
  );
};

export default LikeButton;